import { useState, useMemo } from "react";

const SEGMENT_CONFIG = [
  { key: "Total Active Customers", shortLabel: "Active", color: "#5B8DEF" },
  { key: "Inactive (3M)", shortLabel: "Inactive", color: "#E23D3D" },
  { key: "Reactivated (3M)", shortLabel: "Reactivated", color: "#2FB897" },
];

const mono = "'JetBrains Mono', 'SF Mono', monospace";
const sans = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export default function CustomerHealthSummary({ data, style }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const record = Array.isArray(data) ? data[0] : data;

  const segments = useMemo(() => {
    if (!record) return [];
    return SEGMENT_CONFIG.map((cfg) => ({
      ...cfg,
      value: Number(record[cfg.key]) || 0,
    }));
  }, [record]);

  const segmentTotal = segments.reduce((acc, s) => acc + s.value, 0);
  const newCustomers = Number(record?.["New Customers (6M)"]) || 0;

  const centerValue = activeIndex !== null ? segments[activeIndex].value : segmentTotal;
  const centerLabel = activeIndex !== null ? segments[activeIndex].shortLabel : "Total customers";

  const statTiles = [
    ...segments.map((s) => ({ label: s.shortLabel, value: s.value, color: s.color })),
    { label: "New (6M)", value: newCustomers, color: "#8A93A3" },
  ];

  // Compact geometry for the half-circle arc
  const cx = 100;
  const cy = 102;
  const radius = 76;
  const strokeWidth = 20; // Slimmer arc stroke
  const circumference = 2 * Math.PI * radius;
  const halfCircumference = circumference / 2;
  const gapLen = segments.length > 1 ? 3 : 0;

  let cumulative = 0;
  const arcs = segments.map((s, i) => {
    const fraction = segmentTotal > 0 ? s.value / segmentTotal : 0;
    const rawLen = fraction * halfCircumference;
    const dash = Math.max(rawLen - gapLen, 0);
    const offset = cumulative;
    cumulative += rawLen;
    return { ...s, dash, offset, index: i };
  });

  return (
    <div
      style={{
        fontFamily: sans,
        background: "#FFFFFF",
        border: "1px solid #E5E7EB", 
        borderRadius: "14px",       
        padding: "12px 14px", // Tightened outer padding
        maxWidth: 440,
        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        ...style,
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "6px" }}>
        <div
          style={{
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.05em",
            color: "#6B7280",
            textTransform: "uppercase",
          }}
        >
          Customer Health Summary
        </div>
      </div>

      {/* Raw stats grid (highly minimized heights/paddings) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 5,
          marginBottom: "10px",
        }}
      >
        {statTiles.map((t) => (
          <div
            key={t.label}
            style={{
              background: "#F9FAFB",
              borderRadius: 8,
              padding: "6px 2px",
              textAlign: "center",
              border: "1px solid #F3F4F6",
            }}
          >
            <div style={{ fontFamily: mono, fontSize: 13, fontWeight: 650, color: t.color }}>
              {t.value.toLocaleString()}
            </div>
            <div style={{ fontSize: "9.5px", color: "#6B7280", marginTop: 1, fontWeight: 550 }}>
              {t.label}
            </div>
          </div>
        ))}
      </div>

      {/* Arc chart wrapper (Height condensed down to 105px) */}
      <div style={{ position: "relative", width: "100%", height: 105, margin: "0 auto" }}>
        <svg
          viewBox="0 0 200 110"
          width="100%"
          height="100%"
          style={{ display: "block", overflow: "visible" }}
        >
          {/* track */}
          <path
            d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
            fill="none"
            stroke="#F3F4F6"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <g transform={`rotate(180 ${cx} ${cy})`}>
            {arcs.map((s) => {
              if (s.value <= 0) return null;
              const isDimmed = activeIndex !== null && activeIndex !== s.index;
              return (
                <circle
                  key={s.key}
                  cx={cx}
                  cy={cy}
                  r={radius}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={`${s.dash} ${circumference - s.dash}`}
                  strokeDashoffset={-s.offset}
                  opacity={isDimmed ? 0.35 : 1}
                  style={{ transition: "opacity 160ms ease, stroke-dasharray 700ms ease" }}
                  onMouseEnter={() => setActiveIndex(s.index)}
                  onMouseLeave={() => setActiveIndex(null)}
                />
              );
            })}
          </g>
        </svg>

        <div
          style={{
            position: "absolute",
            bottom: 2,
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <div style={{ fontFamily: mono, fontSize: 22, fontWeight: 700, color: "#111827", lineHeight: 1 }}>
            {centerValue.toLocaleString()}
          </div>
          <div style={{ fontSize: "10.5px", fontWeight: 550, color: "#6B7280", marginTop: 2 }}>
            {centerLabel}
          </div>
        </div>
      </div>

      {/* Legend list (Tight margins & compact padding) */}
      <div 
        style={{ 
          display: "flex", 
          flexDirection: "column", 
          gap: 2, 
          marginTop: "6px", 
          borderTop: "1px solid #F3F4F6", 
          paddingTop: "8px" 
        }}
      >
        {segments.map((s, i) => {
          const pct = segmentTotal > 0 ? Math.round((s.value / segmentTotal) * 100) : 0;
          return (
            <div
              key={s.key}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "3px 4px",
                borderRadius: 6,
                background: activeIndex === i ? "#F9FAFB" : "transparent",
                transition: "background 140ms ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 10, height: 3, borderRadius: 1, background: s.color, display: "inline-block" }} />
                <span style={{ fontSize: "11.5px", color: "#4B5563", fontWeight: 500 }}>
                  {s.key}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span style={{ fontFamily: mono, fontSize: "11.5px", fontWeight: 600, color: "#111827" }}>
                  {s.value.toLocaleString()}
                </span>
                <span style={{ fontSize: "10.5px", color: "#9CA3AF", width: 26, textAlign: "right" }}>
                  {pct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}